import { Nudge } from "@/components/nudges/Nudge";

import { NudgeKeys, useNudges } from "@/hooks/use-nudges";
import { usePreferenceValue } from "@/hooks/use-preference-value";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { TrackLocations } from "@/hooks/use-segment";
import { useHasArchivedEnvironments } from "@/queries/environment-queries";
import { useOrganization } from "@/queries/organization-queries";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";

type ArchivedEnvironmentNudgeProps = {
    setIsArchiveView?: (isArchive: boolean) => void;
};

export const ArchivedEnvironmentNudge: FC<ArchivedEnvironmentNudgeProps> = ({ setIsArchiveView }) => {
    const { active: nudge, error: nudgeError, isPending: isNudgePending } = useNudges();
    const {
        value: archivePreferenceValue,
        setValue: setArchivePreference,
        queryData: { isPending: isPreferencesPending, error: preferencesError },
    } = usePreferenceValue(NudgeKeys.ARCHIVE_FEATURE_INTRODUCED);
    const {
        hasArchivedEnvironments,
        isPending: isEnvironmentsPending,
        error: environmentsError,
    } = useHasArchivedEnvironments();
    const { data: organization } = useOrganization();
    const [open, setOpen] = useState(false);

    // Get tier-specific archive threshold message
    const archiveThresholdDays = useMemo(() => {
        const tier = organization?.tier;
        if (tier === OrganizationTier.FREE_ONA) {
            return 2;
        } else if (tier === OrganizationTier.CORE) {
            return 3;
        }
        return 7; // Default for FREE and ENTERPRISE
    }, [organization?.tier]);

    const handleReview = useCallback(() => {
        setIsArchiveView?.(true);
        setArchivePreference("true");
        setOpen(false);
    }, [setIsArchiveView, setArchivePreference]);

    const handleDismiss = useCallback(() => {
        setOpen((previousOpenState) => {
            const isNowOpen = !previousOpenState;
            if (!isNowOpen) {
                setArchivePreference("true");
            }
            return isNowOpen;
        });
    }, [setArchivePreference]);

    // Auto-show nudge if applicable
    useEffect(() => {
        if (
            isNudgePending ||
            nudgeError ||
            isEnvironmentsPending ||
            environmentsError ||
            isPreferencesPending ||
            preferencesError
        ) {
            return;
        }

        const hasSeenArchiveFeature = archivePreferenceValue === "true";

        if (nudge?.key === NudgeKeys.ARCHIVE_FEATURE_INTRODUCED && !hasSeenArchiveFeature && hasArchivedEnvironments) {
            setOpen(true);
            return;
        }

        setOpen(false);
    }, [
        isNudgePending,
        nudgeError,
        isEnvironmentsPending,
        environmentsError,
        isPreferencesPending,
        preferencesError,
        nudge?.key,
        archivePreferenceValue,
        hasArchivedEnvironments,
    ]);

    const ariaLabelPrefix = "Announcement - Archived Environment";

    return (
        <Nudge
            open={open}
            toggleOpen={handleDismiss}
            ariaLabelPrefix={ariaLabelPrefix}
            showCloseButton={false}
            data-testid="nudge-archived-environment-trigger"
            data-track-location={TrackLocations.ArchivedEnvironmentNudge}
        >
            <Nudge.Content>
                <Nudge.Header>
                    <Nudge.Title>
                        We&apos;ve archived an environment that wasn&apos;t used in {archiveThresholdDays} days
                    </Nudge.Title>
                </Nudge.Header>
                <Nudge.Action
                    variant="primary"
                    aria-label={`${ariaLabelPrefix} - Review archived environments`}
                    onClick={handleReview}
                    data-tracking-id="review-archived-environments-nudge"
                >
                    Review
                </Nudge.Action>
                <Nudge.Action
                    variant="secondary"
                    aria-label={`${ariaLabelPrefix} - Dismiss`}
                    onClick={handleDismiss}
                    data-tracking-id="dismiss-archived-environments-nudge"
                >
                    Dismiss
                </Nudge.Action>
            </Nudge.Content>
        </Nudge>
    );
};
